import React, { useState, useMemo } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { CodingLanguagesMap } from './constants';
import { Base64 } from 'js-base64';

import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import makeStyles from '@mui/styles/makeStyles';
import TabSwitch from 'components/TabSwitch';
import Footer from './Footer';
import ExpandedCodeViewer from './ExpandedCodeEditor';

const useStyles = makeStyles((theme) => ({
  container: {
    backgroundColor: theme.palette.shades['clr-white-900'],
    border: `1px solid ${theme.palette.neutral['clr-200']}`,
    borderRadius: theme.spacing(2),
    width: '100%',
    boxShadow: `
      0px 4px 4px 0px #CCD4DE1A,
      0px 10px 20px 0px #CCD4DE29,
      0px 0px 4px 0px #CCD4DE`,
    display: 'flex',
    flexDirection: 'column',
  },
  description: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(4),
    border: `1px solid ${theme.palette.neutral['clr-200']}`,
    height: '100%',
  },
  codeOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backdropFilter: 'blur(2px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 6,
    background: `#003E8C33`,
  },
  overlayContent: {
    backgroundColor: '#FEE2E2',
    borderRadius: 4,
    padding: 8,
    width: '302px',
    height: '120px',
    color: '#B91C1C',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  expandIcon: {
    height: '40px',
    width: '40px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: theme.spacing(1),
    position: 'absolute',
    right: '10px',
    top: '10px',
    zIndex: 5,
    backgroundColor: theme.palette.shades['clr-white-900']
  }, fullIcon: {
    color: '#003E8C',
    cursor: 'pointer',
    position: 'absolute',
    right: '12px',
    top: '10px',
  }
}));

export const CodeViewer = ({ code, Icon, width, decoded }) => {
  const classes = useStyles();

  const showMissingCode = useMemo(() => {
    return !code?.trim();
  }, [code]);

  const renderCodeBlock = ({ decoded, code }) => {
    const decodedCode = decoded ? code : Base64.decode(code);
  
    return (
      <Box overflow="auto">
        {decoded ? (
          <Typography
            variant="body01-medium"
            whiteSpace="pre-line"
            dangerouslySetInnerHTML={{ __html: decodedCode }}
          />
        ) : (
          <pre
            style={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              margin: 0,
              padding: '8px',
              fontFamily: 'monospace',
              fontSize: '0.9rem',
            }}
          >
            <code>{decodedCode}</code>
          </pre>
        )}
      </Box>
    );
  };

  return (
    <Box className={classes.description} position="relative" width={width}>

      {
        showMissingCode ? <Box className={classes.codeOverlay}>
          <Box className={classes.overlayContent}>
            <WarningAmberIcon sx={{ fontSize: 36 }} />
            <Typography variant="body01-bold">
              The coding solution is missing for this question.
            </Typography>
          </Box>
        </Box> :
          <>
            <Typography variant="h6-semiBold" gutterBottom>Source Code</Typography>
            <IconButton className={classes.fullIcon} onClick={Icon.onClick}>
              {Icon.icon}
            </IconButton>
            <Box overflow='auto'>
             {renderCodeBlock({ decoded, code })}
            </Box>
          </>
      }

    </Box>
  );
}

const CodeViewerWithDescription = ({ code, description, setOpenExpandedCode, decoded, currentKey = 0 }) => {
  const classes = useStyles();

  const showFallbackDescription = useMemo(() => {
    return !description?.trim();
  }, [description]);

  return (
    <Box display="flex" height={300}>

      <CodeViewer
        decoded={decoded}
        width="50%"
        Icon={{
          icon: <OpenInFullIcon />,
          onClick: () => setOpenExpandedCode(true),
        }}
        code={code}
      />

      {/* Description Box */}
      <Box className={classes.description} width="50%">
        <Typography variant="h6-semiBold" gutterBottom>
          {currentKey == 0 ? 'Logical explanation' : 'Your explanation'}
        </Typography>
        <Box overflow='auto'>
          <Typography
            variant="body01-semiBold"
            whiteSpace="pre-line"
            color={showFallbackDescription ? 'danger.clr-700' : ''}
          >
            {showFallbackDescription
              ? 'This part is missing an explanation. Make sure to share your approach when the interviewer prompts you.'
              : description}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default function CodeReviewCard({
  challenge, analysis = [], codeSubmissions = [], hideHeading = false
}) {
  const classes = useStyles();

  const [activeTab, setActiveTab] = useState(0);
  const [questionTab, setQuestionTab] = useState(0);
  const [openExpandedCode, setOpenExpandedCode] = useState(false);

  const [codeMap, setCodeMap] = useState({
    '0': '',   // Optimal code 
    '1-0': '', // User response - Q1 code
    '1-1': '', // User response - F1 code
    '1-2': '', // User response - F2 code
  });

  const [descMap, setDescMap] = useState({
    '0': '',   //Optimal solution explanation
    '1-0': '', //User explanation for Q1
    '1-1': '', //User explanation for F1
    '1-2': '', //User explanation for F2
  });

  React.useEffect(() => {
    let mainCode, folloup1Code, followup2Code = '';
    let mainDesc, followup1Desc, followup2Desc = '';

    const {
      code: languageCode
    } = CodingLanguagesMap[codeSubmissions[0]?.languageId?.toString() || '105'] || { code: 'c++' };

    const indexOfSolution = ['c++', 'java', 'python'].indexOf(languageCode);

    let optimalCode = challenge?.idealSolutions?.[indexOfSolution]?.code || challenge?.idealSolutions?.[0]?.code || '';
    let optimalDesc = challenge?.logicalExplanation || '';

    for (const submission of codeSubmissions) {
      const { questionType, sourceCode } = submission;

      switch (true) {
        case questionType === 'main':
          mainCode = sourceCode;
          break;
        case questionType === 'follow_up_1':
          folloup1Code = sourceCode;
          break;
        case questionType === 'follow_up_2':
          followup2Code = sourceCode;
          break;
        default:
          break;
      }
    }

    for (const data of analysis) {
      const { questionType, explanation } = data;

      switch (true) {
        case questionType === 'main':
          mainDesc = explanation;
          break;
        case questionType === 'follow_up_1':
          followup1Desc = explanation;
          break;
        case questionType === 'follow_up_2':
          followup2Desc = explanation;
          break;
        default:
          break;
      }
    }

    setCodeMap({ '0': optimalCode, '1-0': mainCode, '1-1': folloup1Code, '1-2': followup2Code });
    setDescMap({ '0': optimalDesc, '1-0': mainDesc, '1-1': followup1Desc, '1-2': followup2Desc });

  }, [challenge, codeSubmissions, analysis]);

  const currentKey = useMemo(() => {
    return activeTab === 0 ? '0' : `1-${questionTab}`;
  }, [activeTab, questionTab]);

  const handleActiveTabChange = (e) => {
    setActiveTab(e.id);
  };

  const handleQuestionTabChange = (e) => {
    setQuestionTab(e.id);
  };

  const handleCodeChange = (newCode) => {
    const key = activeTab === 0 ? '0' : `1-${questionTab}`;
    setCodeMap(prev => ({
      ...prev,
      [key]: newCode
    }));
  };

  return (
    <Box display="flex" flexDirection="column" gap="12px">
      {!hideHeading && (
        <Typography variant="h4-medium" color="neutral.clr-900">
          Solution
        </Typography>
      )}
      <Box className={classes.container}>
        <Box p={5} display="flex" justifyContent="space-between">
          <TabSwitch
            width="336px"
            height={48}
            options={[
              { id: 0, label: 'Optimal solution' },
              { id: 1, label: 'Your response' },
            ]}
            value={activeTab}
            onChange={handleActiveTabChange}
          />
          {activeTab === 1 && (
            <TabSwitch
              width="336px"
              height={48}
              options={[
                { id: 0, label: 'Q1', tooltip: 'Question 1' },
                { id: 1, label: 'F1', tooltip: 'Follow Up 1' },
                { id: 2, label: 'F2', tooltip: 'Follow Up 2' },
              ]}
              value={questionTab}
              onChange={handleQuestionTabChange}
            />
          )}
        </Box>

        <CodeViewerWithDescription
          code={codeMap[currentKey] || ''}
          decoded={currentKey === '0'}
          setCode={handleCodeChange}
          description={descMap[currentKey] || ''}
          editorKey={currentKey}
          setOpenExpandedCode={setOpenExpandedCode}
          currentKey={currentKey}
        />

        <Footer
          items={[
            {
              content: '*Questions 2 and 3 follow up on Question 1, leading to one optimal solution.',
              type: 'default',
              hide: false
            },
          ]}
          sx={{ paddingTop: '4px', paddingBottom: '4px' }}
        />

      </Box>

      <ExpandedCodeViewer
        open={openExpandedCode}
        onClose={() => setOpenExpandedCode(false)}
        code={codeMap[currentKey] || ''}
        decoded={currentKey === '0'}
        setCode={handleCodeChange}
        editorKey={currentKey}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        questionTab={questionTab}
        setQuestionTab={setQuestionTab}
        setOpenExpandedCode={setOpenExpandedCode}
      />
    </Box>
  );
}